// Item library evaluate codes for /roads-sewers/roads dev-mode display — mirrors
// prelimsMapping.ts's description -> code lookup pattern. Each roads section that
// shows a dev-mode "Evaluate Code" column looks its rows up here by description
// rather than hardcoding codes inline, so codes stay in one place as more are added.

import { BASE_COURSE_OPTIONS, BINDER_COURSE_OPTIONS } from './constants';

export interface RoadsEvaluateCodeItem {
  description: string;
  code: string;
}

export const CARRIAGEWAY_SUMMARY_EVALUATE_CODES: RoadsEvaluateCodeItem[] = [
  { description: 'Site strip to remove vegetation', code: 'ALP033' },
  { description: 'Excavate to reduce levels', code: 'ALP014' },
  { description: 'Filling with site won subsoil to paths', code: 'ALP015' },
  { description: 'Filling with recycled 6F2 to footpaths', code: 'ALP016' },
  { description: 'Filling with recycled 6F2 to roads', code: 'ALP016' },
  { description: 'Level and compact formation', code: 'ALP035' },
  { description: 'Membrane / Geotextile', code: 'ALP031' },
  { description: 'Membrane', code: 'ALP031' },
  { description: 'Hot bond coat between layers', code: 'ALP054' },
  { description: 'Sealing grit', code: 'SCSM001' },
  { description: 'Sealing Grit', code: 'SCSM001' },
  { description: 'Sweep tack', code: 'SCSM002' },
  { description: 'Sweep', code: 'SCSM002' },
  { description: 'Sweep & tack coat', code: 'SCSM002' },
  { description: 'EO Pre Coated Chippings', code: 'SCSM024' },
  { description: 'EO PSV 65+', code: 'SCSM025' },

  // KERBS & EDGINGS (kerbDefs array) — "No Code"/blank rows in the source
  // spec (Temporary Kerb / Block to race, Remove Temporary Kerb / Block,
  // Drop Kerb, Delineation Kerb, EO Radius Kerb, EO Radius Drop Kerb, EO
  // Ramps) are deliberately left unmapped, same as this file's other
  // no-code rows.
  { description: 'Kerb Race', code: 'ALP025a' },
  { description: 'Extruded Kerb Race', code: 'ALP025' },
  { description: 'PCC Kerbs - All Sizes (including race)', code: 'ALP083' },
  { description: '10×5 Kerb on race', code: 'ALP024D' },
  { description: '6×5 Kerb on race', code: 'ALP024D' },
  { description: 'Kassel Bus Stop Kerbs', code: 'ALP092' },
  { description: 'Trief Kerb', code: 'ALP093' },
  { description: 'Beany Drainage Kerb', code: 'ALP096' },
  { description: 'Beany Drainage Gully', code: 'ALP097' },
  { description: 'Aco Kerbdrain 305', code: 'ALP098' },
  { description: 'Aco Kerbdrain 305 Gully', code: 'ALP099' },
  { description: 'EO for 50×50mm key formed in kerb race', code: 'ALP026' },
  { description: 'EO for xxmmØ dowel to kerb race at xxxmm ccs', code: 'ALP081' },
  { description: 'Temporary macadam fillet to drive crossover prior to road surfacing', code: 'ALP107' },
  { description: 'Remove macadam fillet upon completion', code: 'ALP108' },
  { description: '150×50 pcc edging', code: 'ALP027' },
  { description: '200×50 pcc edging', code: 'ALP027A' },
  { description: 'Tactile Paving', code: 'ALP104' },
  { description: 'Corduroy Paving', code: 'ALP105' },

  // ADJUST IRONWORK / WHITE LINING / STREET NAMEPLATES / TREE PITS & BOLLARDS
  // / REMEDIALS / FENCING / SIGNAGE (manual-input rows). "Manholes - double
  // cover and frame" is a deliberate no-code row, same convention as above.
  { description: 'Manholes - single cover and frame', code: 'ALP250' },
  { description: 'Gully covers and frames', code: 'ALP251' },
  { description: 'Stop Tap Boxes', code: 'ALP253' },
  { description: 'BT / Virgin footway boxes', code: 'ALP252' },
  { description: 'White lining', code: 'SCWL001' },
  { description: 'Fix-only street nameplates', code: 'ALP282' },
  { description: 'Tree pits', code: 'ALP200+ALP201' },
  { description: 'Bollards', code: 'ALPTB001' },
  { description: 'Kerb remedial', code: 'ALP085' },
  { description: 'Edging remedial', code: 'ALP086' },
  { description: 'Post & Rail Fencing', code: 'SCFS278001' },
  { description: 'Armco barrier', code: 'SCFS278002' },
  { description: 'Supply & install signs', code: 'SCSB002' },
  { description: 'Keep left Illuminated bollard', code: 'SCSB004' },
  { description: 'Keep left reflective bollard', code: 'SCSB003' },
  { description: '100mm thick AC32 DB base course', code: 'ALP041' },
  { description: '150mm thick AC32 DB base course', code: 'ALP042' },
  { description: '50mm thick AC20 DB binder course', code: 'ALP043' },
  { description: '60mm thick AC20 DB binder course', code: 'ALP044' },
  { description: '75mm thick AC20 DB binder course', code: 'ALP045' },
  { description: '80mm thick AC20 DB binder course', code: 'ALP046' },
  { description: '100mm thick AC20 DB binder course', code: 'ALP047' },
  { description: '20mm thick AC6 DB surface course', code: 'ALP048' },
  { description: '25mm thick AC6 DB surface course', code: 'ALP049' },
  { description: '30mm thick AC6 DB surface course', code: 'ALP051' },
];

export const CAPPING_LAYERS_EVALUATE_CODES: RoadsEvaluateCodeItem[] = [
  { description: 'Omit exc', code: 'ALP014' },
  { description: 'Omit 6F2 fill', code: 'ALP016' },
  { description: 'Add exc', code: 'ALP014' },
  { description: 'Add 6F2 fill', code: 'ALP016' },
  { description: 'Stockpile surplus onsite', code: 'ALP023' },
  { description: 'EO remove surplus arisings from site', code: 'AMAS002' },
];

// "Omit xxmm thick Type 1 to tarmac/block paved road" embeds a live-configured
// mm value (see RoadsTakeoff.tsx's t1ThicknessMm) instead of fixed text —
// matched by pattern rather than exact text so the code still resolves
// whatever thickness is currently configured (or the "xx" placeholder before
// it's configured).
const OMIT_TYPE1_PATTERN = /^Omit (\d+|xx)mm thick Type 1 to (tarmac|block paved) road$/;

// "Excavate to reduce levels to take up <stripMm>mm top soil, deposit onsite"
// embeds the live-configured site-strip depth (see RoadsBillSummary.tsx's
// stripMm) instead of fixed text — matched by pattern like OMIT_TYPE1_PATTERN.
const TOPSOIL_EXCAVATION_PATTERN = /^Excavate to reduce levels to take up \d+mm top soil, deposit onsite$/;

// Tarmac Spec and Block Paved Spec build-up rows (RoadsBillSummary.tsx's
// per-road-type spec block, both branches) embed live-configured
// thickness/material values (or the "xx" placeholder before configured) the
// same way OMIT_TYPE1_PATTERN/TOPSOIL_EXCAVATION_PATTERN do — matched by
// pattern rather than exact text. Block Paved's own text reuses the same
// item at lower case ("Sub base"/"base course"/"binder course" vs Tarmac's
// "Sub Base"/"Base Course"/"Binder Course") so casing is matched either way.
const TARMAC_CAPPING_LAYER_PATTERN = /^(\d+|xx)mm thick .+ layer$/;
const TARMAC_SUB_BASE_PATTERN = /^(\d+|xx)mm thick .+ Sub [Bb]ase$/;
const TARMAC_BASE_COURSE_PATTERN = /^(\d+|xx)mm thick (AC32(?: - [ABC])?) DBM [Bb]ase [Cc]ourse(?:, attached .+)?$/;
const TARMAC_BINDER_COURSE_PATTERN = /^(\d+|xx)mm thick (AC20(?: - [ABC])?) DBM [Bb]inder [Cc]ourse(?:, attached .+)?$/;
const TARMAC_SURFACE_COURSE_PATTERN = /^(\d+|xx)mm thick .+ Surface Course$/;

// Block Paved Spec's surface course row (RoadsBillSummary.tsx) renders as
// just the surface type (from BLOCK_PAVED_SURFACE_COURSE_TYPES, constants.ts)
// optionally suffixed with a sand-blinding thickness, not a "<mm>mm thick ..."
// template like Tarmac's — each of the 6 surface types resolves to its own
// evaluate code (BLOCK_SURFACE_COURSE_CODE_MAP below) rather than one shared code.
const BLOCK_SURFACE_COURSE_PATTERN = /^(200x100x60mm Block Paving|200x100x80mm Block Paving|60mm Tegula Paving|80mm Tegula Paving|200x100x60mm Keybloc Paving|200x100x80mm Keybloc Paving)( laid on \d+mm sand blinding)?$/;
const BLOCK_SURFACE_COURSE_CODE_MAP: Record<string, string> = {
  '200x100x60mm Block Paving': 'GWALPD002',
  '200x100x80mm Block Paving': 'GWALPD001',
  '60mm Tegula Paving': 'GWALPD006',
  '80mm Tegula Paving': 'GWALPD005',
  '200x100x60mm Keybloc Paving': 'GWALPD010',
  '200x100x80mm Keybloc Paving': 'GWALPD009',
};

// Footpaths, Vehicle Crossovers & Verges (RoadsBillSummary.tsx's footpathConfigs
// block — tarmacPath/tarmacCrossover/heavyDutyCrossover/blockPavedPathCrossover/
// cycleway) render their surface course row as "<mm>mm thick <constructionType>
// surface course" (lower-case "surface course", vs Tarmac/Block Spec's own
// capitalized rows above) where constructionType is one of FootpathConstructionType's
// fixed values (tender.types.ts) — same billed item, same SCSTC006A code.
const FOOTPATH_SURFACE_COURSE_PATTERN = /^(\d+|xx)mm thick (AC6 DBM|AC10 DBM|HRA|SMA|60mm Block Paving|80mm Block Paving|60mm Tegula Paving|80mm Tegula Paving|60mm Keyblok|80mm Keyblok) surface course$/;

// "Add <mm>mm <material>" (the capping layer's own thickness label, e.g.
// "A" from cappingThicknesses) resolves its code off the capping material
// dropdown (CAPPING_TYPES in constants.ts) rather than the rendered text —
// looked up directly by RoadsTakeoff.tsx via findCappingMaterialEvaluateCode,
// not through findCappingLayersEvaluateCode's description matching below.
const CAPPING_MATERIAL_CODE_MAP: Record<string, string> = {
  'R/C 6F2 capping': 'ALP016',
  'Q 6F5 capping': 'ALP017b',
  'W75/W150': 'ALP017a',
};

export function findCarriagewaySummaryEvaluateCode(description: string): string | undefined {
  const fixed = CARRIAGEWAY_SUMMARY_EVALUATE_CODES.find((i) => i.description === description)?.code;
  if (fixed) return fixed;
  if (TOPSOIL_EXCAVATION_PATTERN.test(description)) return 'ALP013';
  if (TARMAC_SUB_BASE_PATTERN.test(description)) return 'ALP017c';
  if (TARMAC_CAPPING_LAYER_PATTERN.test(description)) return 'ALP016';
  const baseMatch = description.match(TARMAC_BASE_COURSE_PATTERN);
  if (baseMatch) return BASE_COURSE_OPTIONS.find((o) => o.n === baseMatch[2])?.c ?? 'SCSTC001A';
  const binderMatch = description.match(TARMAC_BINDER_COURSE_PATTERN);
  if (binderMatch) return BINDER_COURSE_OPTIONS.find((o) => o.n === binderMatch[2])?.c ?? 'SCSTC002A';
  if (TARMAC_SURFACE_COURSE_PATTERN.test(description)) return 'SCSTC006A';
  const blockMatch = description.match(BLOCK_SURFACE_COURSE_PATTERN);
  if (blockMatch) return BLOCK_SURFACE_COURSE_CODE_MAP[blockMatch[1]];
  if (FOOTPATH_SURFACE_COURSE_PATTERN.test(description)) return 'SCSTC006A';
  return undefined;
}

export function findCappingLayersEvaluateCode(description: string): string | undefined {
  const fixed = CAPPING_LAYERS_EVALUATE_CODES.find((i) => i.description === description)?.code;
  if (fixed) return fixed;
  if (OMIT_TYPE1_PATTERN.test(description)) return 'ALP017c';
  return undefined;
}

export function findCappingMaterialEvaluateCode(cappingMaterial: string): string | undefined {
  return CAPPING_MATERIAL_CODE_MAP[cappingMaterial];
}
